/**
 * Core HTTP Client
 * Provides base methods for making authenticated API requests
 */

import type { ApiResponse } from "./types";
import { tokenStorage } from "./token-storage";

const API_PATH = "/api/v1/creator_portal";

/**
 * Get the MagicLinks API host from the meta tag set by Rails
 * Falls back to empty string (same-origin) for development flexibility
 */
export const getApiHost = (): string => {
  if (typeof document === "undefined") return "";
  const metaTag = document.head.querySelector(
    'meta[name="magiclinks-api-host"]',
  );
  return metaTag ? (metaTag as HTMLMetaElement).content : "";
};

/**
 * Get headers for API requests
 */
const getHeaders = (includeAuth: boolean = true): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (includeAuth) {
    const token = tokenStorage.getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  return headers;
};

/**
 * Build full URL for API endpoint
 */
export const buildUrl = (path: string): string => {
  const host = getApiHost();
  return `${host}${API_PATH}${path}`;
};

/**
 * Decode the payload of a JWT and check whether it has expired.
 * Adds a 30-second buffer so we refresh slightly before actual expiry.
 */
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return false;
    const bufferSeconds = 30;
    return decoded.exp - bufferSeconds < Date.now() / 1000;
  } catch {
    return true;
  }
};

/**
 * In-flight token exchange promise to prevent duplicate requests.
 * Multiple concurrent API calls will all await the same exchange.
 */
let tokenExchangePromise: Promise<boolean> | null = null;

/**
 * Ensure a valid (non-expired) token exists before making an API request.
 * If the token is missing or expired, attempts a lazy exchange via the
 * ml-reactjs proxy. Returns true if a usable token is available.
 */
export const ensureToken = async (): Promise<boolean> => {
  const existing = tokenStorage.getToken();
  if (existing && !isTokenExpired(existing)) return true;

  // Token is missing or expired — clear stale value and re-exchange
  if (existing) {
    tokenStorage.removeToken();
  }

  // Deduplicate concurrent exchange attempts
  if (tokenExchangePromise) return tokenExchangePromise;

  tokenExchangePromise = (async () => {
    try {
      const { auth } = await import("./auth");
      const result = await auth.exchangeToken();
      if (!result.success) {
        console.warn("magiclinks-api token exchange failed:", result.error);
      }
      return result.success;
    } catch (error) {
      console.error("magiclinks-api token exchange error:", error);
      return false;
    } finally {
      tokenExchangePromise = null;
    }
  })();

  return tokenExchangePromise;
};

/**
 * Core HTTP client methods
 */
export const httpClient = {
  /**
   * Make an authenticated GET request that returns a Blob (e.g. for images)
   */
  getBlob: async (path: string): Promise<Blob> => {
    await ensureToken();
    const response = await fetch(buildUrl(path), {
      method: "GET",
      headers: getHeaders(true),
    });
    if (!response.ok) throw new Error("Failed to fetch");
    return response.blob();
  },

  /**
   * Make an authenticated GET request
   */
  get: async <T = unknown>(path: string): Promise<ApiResponse<T>> => {
    try {
      await ensureToken();
      const response = await fetch(buildUrl(path), {
        method: "GET",
        headers: getHeaders(true),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.errors || data.error || "Something went wrong. Please try again later.",
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error("API GET error:", error);
      return { success: false, error: "Network error" };
    }
  },

  /**
   * Make an authenticated POST request
   */
  post: async <T = unknown>(
    path: string,
    body: object,
  ): Promise<ApiResponse<T>> => {
    try {
      await ensureToken();
      const response = await fetch(buildUrl(path), {
        method: "POST",
        headers: getHeaders(true),
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.errors || data.error || "Something went wrong. Please try again later.",
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error("API POST error:", error);
      return { success: false, error: "Network error" };
    }
  },

  /**
   * Make an authenticated PUT request
   */
  put: async <T = unknown>(
    path: string,
    body: object,
  ): Promise<ApiResponse<T>> => {
    try {
      await ensureToken();
      const response = await fetch(buildUrl(path), {
        method: "PUT",
        headers: getHeaders(true),
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.errors || data.error || "Something went wrong. Please try again later.",
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error("API PUT error:", error);
      return { success: false, error: "Network error" };
    }
  },

  /**
   * Make an authenticated DELETE request
   */
  delete: async <T = unknown>(path: string, body?: object): Promise<ApiResponse<T>> => {
    try {
      await ensureToken();
      const options: RequestInit = {
        method: "DELETE",
        headers: getHeaders(true),
      };
      if (body) {
        options.body = JSON.stringify(body);
      }
      const response = await fetch(buildUrl(path), options);

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.errors || data.error || "Something went wrong. Please try again later.",
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error("API DELETE error:", error);
      return { success: false, error: "Network error" };
    }
  },

  /**
   * Make an authenticated PUT request with FormData (for file uploads)
   */
  putFormData: async <T = unknown>(
    path: string,
    formData: FormData,
  ): Promise<ApiResponse<T>> => {
    try {
      await ensureToken();
      const token = tokenStorage.getToken();
      const headers: HeadersInit = { Accept: "application/json" };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const response = await fetch(buildUrl(path), {
        method: "PUT",
        headers,
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.errors || data.error || "Something went wrong. Please try again later.",
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error("API PUT FormData error:", error);
      return { success: false, error: "Network error" };
    }
  },

  /**
   * Make an authenticated POST request with FormData (for file uploads)
   */
  postFormData: async <T = unknown>(
    path: string,
    formData: FormData,
  ): Promise<ApiResponse<T>> => {
    try {
      await ensureToken();
      const token = tokenStorage.getToken();
      const headers: HeadersInit = { Accept: "application/json" };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const response = await fetch(buildUrl(path), {
        method: "POST",
        headers,
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.errors || data.error || "Something went wrong. Please try again later.",
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error("API POST FormData error:", error);
      return { success: false, error: "Network error" };
    }
  },

  /**
   * Make an unauthenticated GET request (for public endpoints)
   */
  getUnauthenticated: async <T = unknown>(
    path: string,
  ): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(buildUrl(path), {
        method: "GET",
        headers: getHeaders(false),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.errors || data.error || "Something went wrong. Please try again later.",
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error("API GET error:", error);
      return { success: false, error: "Network error" };
    }
  },

  /**
   * Make an unauthenticated POST request (for login, signup)
   */
  postUnauthenticated: async <T = unknown>(
    path: string,
    body: object,
  ): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(buildUrl(path), {
        method: "POST",
        headers: getHeaders(false),
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.errors || data.error || "Something went wrong. Please try again later.",
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error("API POST error:", error);
      return { success: false, error: "Network error" };
    }
  },

  /**
   * Make an unauthenticated DELETE request
   */
  deleteUnauthenticated: async <T = unknown>(
    path: string,
    body?: object,
  ): Promise<ApiResponse<T>> => {
    try {
      const options: RequestInit = {
        method: "DELETE",
        headers: getHeaders(false),
      };
      if (body) {
        options.body = JSON.stringify(body);
      }
      const response = await fetch(buildUrl(path), options);

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.errors || data.error || "Something went wrong. Please try again later.",
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error("API DELETE error:", error);
      return { success: false, error: "Network error" };
    }
  },
};
